import { TextButton } from "@/atoms/text-button/TextButton";
import { useTranslation } from "@/core/i18n";
import { openUrl } from "@/utils/openUrl/openUrl";

// A bare hostname: at least one dot, no scheme, no whitespace, no leading slash or dot.
const BARE_HOSTNAME = /^[a-z0-9-]+(\.[a-z0-9-]+)+(\/\S*)?$/i;

/**
 * Normalises a practice-supplied destination to an absolute web URL, or rejects it.
 *
 * These values are typed into admin by hand, so "revivemedspa.com" and "www.revivemedspa.com"
 * are as likely as a full URL. Passed through as-is, a scheme-less value is treated as a
 * *relative* path and would navigate inside the patient app instead of leaving it. Anything
 * that is neither an http(s) URL nor a plausible hostname (a relative path, `mailto:`, or a
 * scheme like `javascript:`) is rejected so the caller falls through to the next option
 * rather than opening a broken or hostile destination.
 */
export const toAbsoluteWebUrl = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return undefined;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return BARE_HOSTNAME.test(trimmed) ? `https://${trimmed}` : undefined;
};

const firstUsableUrl = (...candidates: (string | null | undefined)[]): string | undefined => {
  for (const candidate of candidates) {
    const url = toAbsoluteWebUrl(candidate);
    if (url) {
      return url;
    }
  }
  return undefined;
};

// "No thanks" escape hatch, required by Twilio's compliance review (2026-07): the user must
// be able to leave the SMS opt-in flow. Shown on the sign-up screen both before and after a
// number is entered.
//
// Where it goes, in order:
//   1. the practice's own website (PlatformModel.Website), when set;
//   2. otherwise the booking URL of the platform's earliest active location that defines one
//      (PlatformModel.BookAppointmentUrl, resolved server-side);
//   3. otherwise nowhere — it resets the sign-up screen instead, so the user is not left
//      staring at the number they just typed.
// The link is always rendered: the third tier guarantees it always has something to do.
type ExitSignUpLinkProps = {
  /** The practice's marketing website — first choice. */
  websiteUrl?: string | null;
  /** Booking URL of the earliest active location that has one — second choice. */
  bookAppointmentUrl?: string | null;
  /** Last resort when neither URL exists: return the screen to its initial, empty state. */
  onReset: () => void;
  className?: string;
};

export const ExitSignUpLink = ({
  websiteUrl,
  bookAppointmentUrl,
  onReset,
  className = "",
}: ExitSignUpLinkProps) => {
  const { t } = useTranslation();
  const destination = firstUsableUrl(websiteUrl, bookAppointmentUrl);

  const handleClick = () => {
    if (destination) {
      openUrl(destination);
      return;
    }
    onReset();
  };

  return (
    <TextButton xsText className={`mx-auto ${className}`} onClick={handleClick}>
      {t("exitSignUpLink.noThanks")}
    </TextButton>
  );
};
